import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Page, PageMedia, PageVersion, PageFormValues } from '@/types/page';
import { pageService } from '@/services/pageService';

interface UsePageContentOptions {
  isAdmin?: boolean;
  customSlug?: string;
}

export const usePageContent = (optionsOrIsAdmin: UsePageContentOptions | boolean = false) => {
  // Support both old signature (boolean) and new signature (options object)
  const options: UsePageContentOptions = typeof optionsOrIsAdmin === 'boolean'
    ? { isAdmin: optionsOrIsAdmin }
    : optionsOrIsAdmin;
  
  const { isAdmin = false, customSlug } = options;
  
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Determine the slug: customSlug > URL params > pathname
  const slug = customSlug || paramSlug || location.pathname.split('/').filter(Boolean).pop() || 'home';
  
  const [page, setPage] = useState<Page | null>(null);
  const [media, setMedia] = useState<PageMedia[]>([]);
  const [versions, setVersions] = useState<PageVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchPage = useCallback(async () => {
    if (!slug) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const [pageResult] = await Promise.all([
        pageService.getPageBySlug(slug, { isAdmin })
      ]);
      
      if (pageResult.success) {
        setPage(pageResult.data);
      } else {
        // Don't set error for "not found" - let PageRenderer handle it with fallback
        if (pageResult.error.code === 'NOT_FOUND') {
          setPage(null);
        } else {
          setError(pageResult.error);
        }
      }
      
      // Media is now embedded in content via rich text editor - no separate media table needed
      setMedia([]);
      
      if (isAdmin && pageResult.success && pageResult.data) {
        const versionsResult = await pageService.getPageVersions(pageResult.data.id);
        if (versionsResult.success) {
          setVersions(versionsResult.data);
        }
      }
    } catch (err) {
      console.error('Error fetching page:', err);
      setError(err instanceof Error ? err : new Error('Failed to load page'));
    } finally {
      setIsLoading(false);
    }
  }, [slug, isAdmin]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const updatePage = async (data: Partial<PageFormValues>) => {
    if (!page) return;
    
    try {
      const result = await pageService.updatePage(page.id, data);
      if (result.success) {
        setPage(result.data);
        toast.success('Page updated successfully');
        return result.data;
      } else {
        toast.error(result.error.getUserMessage());
        throw result.error;
      }
    } catch (err) {
      console.error('Error updating page:', err);
      if (!(err instanceof Error && err.message.includes('Failed to update'))) {
        toast.error('Failed to update page');
      }
      throw err;
    }
  };

  const uploadMedia = async (file: File) => {
    if (!page) return;
    
    try {
      const result = await pageService.uploadMedia(page.id, file);
      if (result.success) {
        setMedia(prev => [...prev, result.data]);
        toast.success('Media uploaded successfully');
        return result.data;
      } else {
        toast.error(result.error.getUserMessage());
        throw result.error;
      }
    } catch (err) {
      console.error('Error uploading media:', err);
      if (!(err instanceof Error && err.message.includes('Failed to upload'))) {
        toast.error('Failed to upload media');
      }
      throw err;
    }
  };

  const deleteMedia = async (mediaId: string) => {
    try {
      const result = await pageService.deleteMedia(mediaId);
      if (result.success) {
        setMedia(prev => prev.filter(m => m.id !== mediaId));
        toast.success('Media deleted successfully');
      } else {
        toast.error(result.error.getUserMessage());
        throw result.error;
      }
    } catch (err) {
      console.error('Error deleting media:', err);
      if (!(err instanceof Error && err.message.includes('Failed to delete'))) {
        toast.error('Failed to delete media');
      }
      throw err;
    }
  };

  const revertToVersion = async (version: PageVersion) => {
    if (!page) return;
    
    try {
      await updatePage({
        title: version.title,
        content: version.content,
        slug: page.slug,
        is_published: page.is_published
      });
      
      toast.success('Page reverted to selected version');
    } catch (err) {
      console.error('Error reverting version:', err);
      toast.error('Failed to revert to this version');
      throw err;
    }
  };

  const createNewPage = async (data: Omit<PageFormValues, 'id'>) => {
    try {
      const result = await pageService.createPage(data);
      if (result.success) {
        toast.success('Page created successfully');
        navigate(`/admin/pages/${result.data.slug}`);
        return result.data;
      } else {
        toast.error(result.error.getUserMessage());
        throw result.error;
      }
    } catch (err) {
      console.error('Error creating page:', err);
      if (!(err instanceof Error && err.message.includes('Failed to create'))) {
        toast.error('Failed to create page');
      }
      throw err;
    }
  };

  const deletePage = async () => {
    if (!page) return;
    
    try {
      const result = await pageService.deletePage(page.id);
      if (result.success) {
        toast.success('Page deleted successfully');
        navigate('/admin/pages');
      } else {
        toast.error(result.error.getUserMessage());
        throw result.error;
      }
    } catch (err) {
      console.error('Error deleting page:', err);
      if (!(err instanceof Error && err.message.includes('Failed to delete'))) {
        toast.error('Failed to delete page');
      }
      throw err;
    }
  };

  return {
    page,
    media,
    versions,
    isLoading,
    error,
    updatePage,
    uploadMedia,
    deleteMedia,
    revertToVersion,
    createNewPage,
    deletePage,
    refetch: fetchPage
  };
};
